import { domainsWithWhiteText } from "@appcaire/brand";
import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../../lib/utils";
import { domainClasses, type Domain } from "./domains";

export interface DomainCardProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "color" | "style"
> {
  domain: Domain;
  surface?: "tint" | "white" | "solid";
  /** marketing = radius 24 + hover lift; product = rounded-xl + shadow-soft. */
  density?: "marketing" | "product";
  interactive?: boolean;
  children: ReactNode;
}

/** The single card base for Brand 2.2. Takes meaning, never a colour. */
export function DomainCard({
  domain,
  surface = "tint",
  density = "marketing",
  interactive = false,
  className,
  children,
  ...rest
}: DomainCardProps) {
  const d = domainClasses[domain];
  // A solid card carries white text, so only domains that pass 4.5:1 may be
  // solid; the others fall back to their tint.
  const fill =
    surface === "solid" && !domainsWithWhiteText.includes(domain)
      ? "tint"
      : surface;
  return (
    <div
      data-domain={domain}
      className={cn(
        "flex flex-col",
        density === "marketing"
          ? "gap-3.5 rounded-3xl p-6"
          : "gap-3 rounded-xl p-[18px] shadow-soft",
        fill === "tint" && d.tint,
        fill === "white" && "border border-caire-hairline bg-caire-surface",
        fill === "solid" && cn(d.solid, "text-caire-on-solid"),
        interactive &&
          density === "marketing" &&
          "transition-transform duration-200 hover:-translate-y-[3px] motion-reduce:transition-none motion-reduce:hover:translate-y-0",
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}
